import { useMutation } from "@apollo/client";
import { contentTypeAPIs, getScheduleErrorText } from "@platformx/authoring-apis";
import {
  ContentPublishPlan,
  ShowToastError,
  appendBrandNameIfPresent,
  applyPublishStep,
  handleHtmlTags,
  runPublishSteps,
  trimString,
} from "@platformx/utilities";
import { useTranslation } from "react-i18next";
import { brandFieldsToSave, resolveCanonicalPath } from "../../utils/canonicalPath";

const useQuizAPI = () => {
  const { t, i18n } = useTranslation();
  const [publishquizmutate] = useMutation(contentTypeAPIs.publishContentType);
  const [createquizmutate] = useMutation(contentTypeAPIs.createContentType);
  const createQuiz = async (
    pageState,
    IsDuplicate,
    quizState,
    quizInstance,
    updateTempObj,
    isFeatured,
    isShared = false,
  ) => {
    try {
      const newTempData = JSON.parse(JSON.stringify(quizInstance));
      const quesArr = quizState.questions.map((value) => value.page || value.name);
      const tempObjField = {
        questions: [...quesArr],
        background_content: {
          ext: quizState?.original_image.ext,
          objectType: quizState?.imagevideoURL ? "image" : quizState?.colorCode ? "color" : "",
          Url: quizState?.imagevideoURL,
          Title: "",
          Thumbnail: quizState?.imagevideoURL,
          Color: quizState?.colorCode,
        },
        display_scores: quizState?.scoreBy,
        result_range_1: quizState?.result_range_1,
        result_range_2: quizState?.result_range_2,
        result_range_3: quizState?.result_range_3,
        result_range_4: quizState?.result_range_4,
        published_images: quizState?.published_images,
        original_image: quizState?.original_image,
      };
      const quizToSend = {
        ...newTempData,
        CommonFields: {
          ...newTempData.CommonFields,
          ...updateTempObj.current,
          page_state: pageState,
          structure_data: quizState?.structure_data,
          IsConfirm: IsDuplicate,
          seo_enable: quizState?.seo_enable,
          analytics_enable: quizState?.analytics_enable,
          is_featured: isFeatured,
          slug: quizState?.slug,
          canonical_url: resolveCanonicalPath({
            slug: quizState?.slug,
            language: i18n.language,
            tagName: "quiz",
            brandId: quizState?.brand_name,
            stored: quizState?.storedCanonical,
          }),
          ...brandFieldsToSave(quizState),
        },
        ObjectFields: {
          ...newTempData.ObjectFields,
          ...tempObjField,
        },
      };

      const resp = await createquizmutate({
        variables: {
          contenttype: "Quiz",
          input: appendBrandNameIfPresent(quizToSend, isShared),
        },
        context: {
          skipBrandNameHeader: isShared,
        },
      });
      return resp;
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;

      ShowToastError(t("api_error_toast"), statusCode);
    }
  };
  const updateQuizSettings = (quizRef, quizState, pageUrl = "") => {
    // Slug first — it is what the delivery route serves; the doc path
    // (pageUrl) is the pre-slug fallback. The stored canonical wins until the slug changes.
    const canonicalUrl = resolveCanonicalPath({
      slug: quizState?.slug || pageUrl,
      language: i18n.language,
      tagName: "quiz",
      brandId: quizState?.brand_name,
      stored: quizState?.storedCanonical,
    });
    const QuizSettings = {
      socialog_url: canonicalUrl,

      socialog_type: "quiz",
      socialog_sitename: quizRef.current?.title
        ? trimString(handleHtmlTags(quizRef.current?.title), 100)
        : "quiz",
      seo_title: quizRef.current?.title
        ? trimString(handleHtmlTags(quizRef.current?.title), 100)
        : "",
      socialog_title: quizRef.current?.titleSocialShare
        ? trimString(handleHtmlTags(quizRef.current?.titleSocialShare), 100)
        : "",
      socialog_twitter_title: quizRef.current?.titleSocialShare
        ? trimString(handleHtmlTags(quizRef.current?.titleSocialShare), 100)
        : "",
      socialog_description: quizRef.current?.descriptionSocialShare
        ? trimString(handleHtmlTags(quizRef.current?.descriptionSocialShare), 163)
        : "",
      socialog_twitter_description: quizRef.current?.descriptionSocialShare
        ? trimString(handleHtmlTags(quizRef.current?.descriptionSocialShare), 163)
        : "",
      socialog_twitter_url: canonicalUrl,

      keywords: quizState?.tagsSocialShare, //quizRef.current.tags,
      seo_keywords: quizRef.current.tags,
      seo_description: quizRef.current?.description
        ? trimString(handleHtmlTags(quizRef.current?.description), 163)
        : "",
      socialog_image: quizRef.current?.socialShareImgURL,
      socialog_twitter_image: quizRef.current?.socialShareImgURL,
      // KIWI-6576: the schedule is applied when the editor publishes; it is deliberately NOT written
      // into the saved document. A saved "is_schedule_publish: true" would claim a schedule exists
      // when no trigger does (a draft saved with the box ticked, a schedule since cancelled), and
      // the backend's scheduler callback reads those properties. The trigger is the source of truth.
      is_schedule_publish: false,
      schedule_publish_datetime: "",
      is_schedule_unpublish: false,
      schedule_unpublish_datetime: "",
    };
    return QuizSettings;
  };

  /**
   * Publishes the saved Quiz. With no plan, or an unscheduled one, this is the request that was
   * always sent. With a schedule, each step is its own publish call, in order; any failure rejects,
   * so a schedule that did not take effect never looks like a success (KIWI-6576).
   *
   * Returns the wording for a scheduled confirmation as `confirmation`; empty when nothing was
   * scheduled, so the screen shows its normal "published" message.
   */
  const publishQuiz = async (pageURL, plan?: ContentPublishPlan) => {
    try {
      const send = (step?) =>
        publishquizmutate({
          variables: {
            contentType: "Quiz",
            input: step ? applyPublishStep({ page: pageURL }, step) : { page: pageURL },
          },
        });

      if (plan?.isScheduled) {
        await runPublishSteps(plan.steps, send);
        return {
          showPublishConfirm: true,
          // A scheduled Quiz is not live yet, so there is no public URL to offer.
          publishUrl: "",
          confirmation: plan.confirmation ? t(plan.confirmation.key, plan.confirmation.params) : "",
        };
      }

      const resp = await send();
      return {
        showPublishConfirm: true,
        publishUrl: resp?.data?.authoring_publishContent?.current_page_url,
        confirmation: "",
      };
    } catch (error: any) {
      const statusCode = error?.networkError?.statusCode || 0;

      if (error?.name === "SchedulePartialFailureError") {
        // The first schedule is already in place; say so, or the editor resubmits straight into
        // "already trigger is initiated".
        ShowToastError(
          `${t("schedule_partial_failure")} ${getScheduleErrorText(error.cause, t)}`,
          statusCode,
        );
      } else if (error?.graphQLErrors?.length > 0) {
        ShowToastError(getScheduleErrorText(error, t), statusCode);
      } else {
        ShowToastError(t("api_error_toast"), statusCode);
      }
      return { showPublishConfirm: false, publishUrl: "", confirmation: "" };
    }
  };

  return {
    publishQuiz,
    createQuiz,
    updateQuizSettings,
  };
};

export default useQuizAPI;
